import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TiktokShell } from '@/components/tiktok/TiktokShell';
import TiktokCampaigns, { CreateCampaignMenu } from './TiktokCampaigns';
import TiktokAdGroups from './TiktokAdGroups';
import TiktokAds from './TiktokAds';
import TiktokCampaignTree from '@/components/tiktok/TiktokCampaignTree';
import { LayoutList, Megaphone, Layers, Send, X } from 'lucide-react';

// Campaign -> Ad Group -> Ad is a hierarchy, not three unrelated lists, so the
// tabs cross-filter: drilling from a campaign row sets ?campaignId and the Ad
// Groups tab scopes to it. Same model as TikTok's own Ads Manager. All state
// lives in the URL so back/forward and shared links behave. The default
// "Overview" tab is the expandable campaign → ad group → ad tree.
type Tab = 'tree' | 'campaigns' | 'adgroups' | 'ads';

const TABS: Array<{ id: Tab; label: string; icon: React.ReactNode }> = [
  { id: 'tree', label: 'Overview', icon: <LayoutList size={14} /> },
  { id: 'campaigns', label: 'Campaigns', icon: <Megaphone size={14} /> },
  { id: 'adgroups', label: 'Ad Groups', icon: <Layers size={14} /> },
  { id: 'ads', label: 'Ads', icon: <Send size={14} /> },
];

export default function TiktokAdsManager() {
  const [params, setParams] = useSearchParams();
  // Set by the Topbar menu; TiktokCampaigns opens its advanced modal and clears it.
  const [openAdvanced, setOpenAdvanced] = useState(false);

  const rawTab = params.get('tab');
  const tab: Tab = rawTab === 'campaigns' || rawTab === 'adgroups' || rawTab === 'ads' ? rawTab : 'tree';
  const campaignId = params.get('campaignId') ?? undefined;
  const adGroupId = params.get('adGroupId') ?? undefined;

  const go = useCallback(
    (next: { tab: Tab; campaignId?: string; adGroupId?: string }) => {
      const p = new URLSearchParams();
      p.set('tab', next.tab);
      if (next.campaignId) p.set('campaignId', next.campaignId);
      if (next.adGroupId) p.set('adGroupId', next.adGroupId);
      setParams(p);
    },
    [setParams],
  );

  const selectTab = (t: Tab) => {
    // Keep a filter only while it still applies to the level being shown.
    go({
      tab: t,
      campaignId: t === 'adgroups' ? campaignId : undefined,
      adGroupId: t === 'ads' ? adGroupId : undefined,
    });
  };

  const activeFilter =
    tab === 'adgroups' && campaignId
      ? { label: 'Campaign', value: campaignId }
      : tab === 'ads' && adGroupId
        ? { label: 'Ad group', value: adGroupId }
        : null;

  return (
    <TiktokShell
        title="Campaigns"
        section="Campaigns"
        description="Manage campaigns, explore ad groups and keep your creative in view."
        actions={
          <CreateCampaignMenu
            onAdvanced={() => {
              if (tab !== 'campaigns') go({ tab: 'campaigns' });
              setOpenAdvanced(true);
            }}
          />
        }
      >

      {/* Tab bar */}
      <div className="tt-tabs" aria-label="Campaign views">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => selectTab(t.id)}
              aria-pressed={tab === t.id}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
      </div>

      {activeFilter && (
        <div className="flex items-center gap-2 pb-4">
          <span className="text-xs text-text-muted">Filtered by</span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-soft px-2.5 py-1 text-xs font-medium text-primary">
            {activeFilter.label} {activeFilter.value}
            <button
              onClick={() => go({ tab })}
              className="rounded-full p-0.5 hover:bg-primary/20"
              aria-label="Clear filter"
            >
              <X size={11} />
            </button>
          </span>
        </div>
      )}

      <div className="tt-embedded">
      {tab === 'tree' && <TiktokCampaignTree />}
      {tab === 'campaigns' && (
        <TiktokCampaigns
          embedded
          onDrillToAdGroups={(id) => go({ tab: 'adgroups', campaignId: id })}
          autoOpenCreate={openAdvanced}
          onAutoOpenConsumed={() => setOpenAdvanced(false)}
        />
      )}
      {tab === 'adgroups' && (
        <TiktokAdGroups
          embedded
          campaignId={campaignId}
          onDrillToAds={(id) => go({ tab: 'ads', adGroupId: id })}
        />
      )}
      {tab === 'ads' && <TiktokAds embedded adGroupId={adGroupId} />}
      </div>
    </TiktokShell>
  );
}
